import LazyImage from "@eisinc/MobiusOverrides/LazyImage";
import Tooltip from "@eisinc/MobiusOverrides/Tooltip/Tooltip";
import { ProductAvailabilityStyles } from "@eisinc/Overrides/Components/ProductAvailability";
import ProductDescription, { ProductDescriptionStyles } from "@eisinc/Overrides/Components/ProductDescription";
import { ProductPartNumbersStyles } from "@eisinc/Overrides/Components/ProductPartNumbers";
import ProductPrice, { ProductPriceStyles } from "@eisinc/Overrides/Components/ProductPrice";
import MiniMenu from "@eisinc/XDL/Icons/MiniMenu";
import useWidgetBadges from "@insite/client-framework/Common/Hooks/useWidgetBadges";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { VariantDisplayTypeValues } from "@insite/client-framework/Types/ApiModels";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductImageBadges from "@insite/content-library/Components/ProductImageBadges";
import ProductPartNumbers from "@insite/content-library/Components/ProductPartNumbers";
import ProductTextBadges from "@insite/content-library/Components/ProductTextBadges";
import ProductListVariantSwatch from "@insite/content-library/Widgets/ProductList/ProductListVariantSwatch";
import { GridItemProps } from "@insite/mobius/GridItem";
import Icon from "@insite/mobius/Icon";
import Link from "@insite/mobius/Link";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    showBrand: boolean;
    showTitle: boolean;
    showPartNumbers: boolean;
    showAvailability: boolean;
    showAttributes: boolean;
    showImageBadges?: boolean;
    maxImageBadges?: number;
    showTextBadges?: boolean;
    maxTextBadges?: number;
    showPrice?: boolean;
}

const mapStateToProps = (state: ApplicationState) => ({
    settingsCollection: getSettingsCollection(state),
    view: state.pages.productList.view || getSettingsCollection(state).productSettings.defaultViewType,
});

type Props = ReturnType<typeof mapStateToProps> & OwnProps & HasProductContext;

export interface ProductListProductInformationStyles {
    container?: InjectableCss;
    brandStyles?: ProductBrandStyles;
    descriptionStyles?: ProductDescriptionStyles;
    brandGridItem?: InjectableCss;
    descriptionGridItem?: InjectableCss;
    partNumbersGridItem?: InjectableCss;
    swatchGridItem?: GridItemProps;
    imageBadgeGridItem?: GridItemProps;
    textBadgeGridItem?: GridItemProps;
    partNumbersStyles?: ProductPartNumbersStyles;
    availabilityGridItem?: InjectableCss;
    availabilityStyles?: ProductAvailabilityStyles;
    listAvailabilityStyles?: ProductAvailabilityStyles;
    badgesContainerStyle?: InjectableCss;
    productInfoWrapper?: InjectableCss;
    numbersBrandWrapper?: InjectableCss;
    badgesAvaiabilityWrapper?: InjectableCss;
    listInforWrapper?: InjectableCss;
    priceWrapper?: InjectableCss;
    price?: ProductPriceStyles;
    parentPrice?: InjectableCss;
}

export const productInformationStyles: ProductListProductInformationStyles = {
    container: {
        css: css`
            width: 100%;
            &.info-list-view {
                height: 100%;
                > div {
                    &:last-child {
                        height: calc(100% - 51px);
                    }
                }
            }
        `,
    },
    descriptionStyles: {
        productDetailLink: {
            color: "text.main",
            typographyProps: {
                size: 16,
                weight: "normal",
                lineHeight: "20px",
            },
        },
        productDetailDescription: {
            size: 16,
            weight: "normal",
            lineHeight: "20px",
        },
    },
    brandGridItem: {},
    descriptionGridItem: {
        css: css`
            margin-bottom: ${(props: any) => (props?.view === "List" ? "11px" : "4px")};
            height: ${(props: any) => (props?.view === "List" ? "40px" : "60px")};

            * {
                font-size: ${(props: any) => (props?.view === "List" ? "16px" : "14px")};
                line-height: 20px;
            }
        `,
    },
    partNumbersGridItem: {
        css: css`
            display: flex;
            align-items: center;
            margin-bottom: ${(props: any) => (props?.view === "List" ? "11px" : "1px")};
            height: ${(props: any) => (props?.view === "List" ? "auto" : "22px")};
            svg {
                width: 20px;
                height: 24px;
            }
            @media (max-width: 481px) {
                width: 100%;
            }
        `,
    },
    swatchGridItem: {
        css: css`
            width: 100%;
        `,
    },
    imageBadgeGridItem: {
        width: 12,
    },
    textBadgeGridItem: {
        width: 12,
    },
    availabilityGridItem: {},
    badgesContainerStyle: {
        css: css`
            display: flex;
            &.info-grid-view-badges {
                position: absolute;
                top: -18px;
                right: -15px;
                button {
                    padding: 0;
                    width: 26px;
                    height: 26px;
                }
                img {
                    max-width: 26px;
                    max-height: 26px;
                }
            }
            &.info-list-view-badges {
                button {
                    padding: 0;
                    width: 21px;
                    height: 21px;
                }
                img {
                    max-width: 21px;
                    max-height: 21px;
                    overflow: hidden;
                }
            }
        `,
    },
    productInfoWrapper: {
        css: css`
            .product-attr-label,
            .product-attr-value {
                font-size: 14px;
                line-height: 18px;
                font-weight: normal;
            }

            .product-attr-label {
                font-weight: bold;
            }
        `,
    },
    partNumbersStyles: {
        erpNumberLabelText: {
            size: 14,
            weight: "bold",
            lineHeight: "18px",
        },
        customerNameLabelText: {
            size: 14,
            weight: "bold",
            lineHeight: "18px",
        },
        specNumberLabelText: {
            size: 14,
            weight: "bold",
            lineHeight: "18px",
        },
        manufacturerItemLabelText: {
            size: 14,
            weight: "bold",
            lineHeight: "18px",
        },
        erpNumberValueText: {
            size: 14,
            weight: "normal",
            lineHeight: "18px",
        },
        customerNameValueText: {
            size: 14,
            weight: "normal",
            lineHeight: "18px",
        },
        specNumberValueText: {
            size: 14,
            weight: "normal",
            lineHeight: "18px",
        },
        manufacturerItemValueText: {
            size: 14,
            weight: "normal",
            lineHeight: "18px",
        },
    },
    listInforWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            > div {
                flex: 1;
                @media (max-width: 481px) {
                    flex: none;
                    width: 100%;
                }
                &:first-child {
                    padding-right: 15px;
                }
            }
        `,
    },
    numbersBrandWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            justify-content: space-between;
        `,
    },
    badgesAvaiabilityWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            justify-content: space-between;
        `,
    },
    brandStyles: {
        logoImage: {
            width: "auto",
            height: "40px",
            css: css`
                max-width: 100%;

                img {
                    max-width: 100%;
                    max-height: 100%;
                    width: auto;
                    height: auto;
                }
            `,
        },
        nameLink: {
            color: "text.main",
        },
        nameText: {
            color: "text.main",
        },
    },
    listAvailabilityStyles: {
        containerWithAdditionalMessage: {
            css: css`
                display: flex;
                @media only screen and (max-width: 1439px) {
                    flex-direction: column;
                    .separator {
                        display: none;
                    }
                }

                @media (max-width: 481px) {
                    flex-direction: row;
                    .separator {
                        display: block;
                    }
                }
            `,
        },
    },
    priceWrapper: {
        css: css`
            min-height: 22px;
            margin-bottom: 22px;
            display: flex;
            justify-content: flex-start;
            > section {
                width: auto;
            }
            * {
                line-height: 22px;
                font-size: 24px;
                font-weight: normal;
                color: ${getColor("text.main")};
            }
        `,
    },
    parentPrice: {
        css: css`
            display: flex;
            align-items: center;
        `,
    },
};

const styles = productInformationStyles;

const ProductListProductInformation = ({
    productContext,
    settingsCollection,
    showBrand,
    showTitle,
    showPartNumbers,
    showAvailability,
    showAttributes,
    showImageBadges,
    maxImageBadges,
    showTextBadges,
    maxTextBadges,
    view,
    showPrice,
}: Props) => {
    const { product } = productContext;

    if (!product) {
        return null;
    }

    const { imageBadges, textBadges } = useWidgetBadges({ productContext, showTextBadges, showImageBadges });
    let variantDisplayTrait = null;
    const badges = (product.properties["badges"] && JSON.parse(product.properties["badges"])) || [];
    const StyledWrapperX = getStyledWrapper("div") as any;
    const displayHighLowPricesForParentProducts =
        (settingsCollection?.customSettings as any).displayHighLowPricesForParentProducts === "True";

    if (product?.variantTraits && product.variantTraits.length > 0) {
        const swatchedViewList = [
            VariantDisplayTypeValues.SwatchDropdown,
            VariantDisplayTypeValues.SwatchList,
            VariantDisplayTypeValues.SwatchGrid,
        ];
        const sortedVaraintTraits = product.variantTraits.slice().sort((a, b) => a.sortOrder - b.sortOrder);
        variantDisplayTrait = sortedVaraintTraits.find(x => swatchedViewList.includes(x.displayType));
    }

    return (
        <StyledWrapper {...styles.container} className={view === "List" ? "info-list-view" : "info-grid-view"}>
            {view !== "List" && badges?.length > 0 && (
                <StyledWrapper {...styles.badgesContainerStyle} className="info-grid-view-badges">
                    {badges.map(
                        (badge: { Image: string | undefined; DisplayName: string; Description: string }, index: any) =>
                            badge.Image && (
                                <StyledWrapper
                                    css={css`
                                        margin-left: 5px;
                                    `}
                                    // eslint-disable-next-line react/no-array-index-key
                                    key={`badge${index}`}
                                >
                                    <StyledWrapper
                                        className="popup-content-wrapper"
                                        css={css`
                                            max-width: 250px;
                                        `}>
                                        <Tooltip
                                            passAsChildren={true}
                                            triggerComponent={
                                                <StyledWrapper>
                                                    <LazyImage src={badge.Image} />
                                                </StyledWrapper>
                                            }>
                                            <StyledWrapper className="popup-content-wrapper">
                                                {/* eslint-disable react/no-danger */}
                                                <div
                                                    dangerouslySetInnerHTML={{
                                                        __html:
                                                            badge.DisplayName === "PROP_65_CANCER"
                                                                ? product.properties.prop65Text ||
                                                                (siteMessage("Custom_Prop65DefaultText") as string)
                                                                : badge.Description,
                                                    }}
                                                />
                                            </StyledWrapper>
                                        </Tooltip>
                                    </StyledWrapper>
                                </StyledWrapper>
                            ),
                    )}
                </StyledWrapper>
            )}
            {showTitle && (
                <StyledWrapperX view={view} {...styles.descriptionGridItem}>
                    <ProductDescription
                        {...{ widgetId: "PLP" }}
                        product={productContext}
                        extendedStyles={styles.descriptionStyles}
                        showElipses={true}
                        showElipsesOn={view === "List" ? "2" : "3"}
                    />
                </StyledWrapperX>
            )}
            {view !== "List" && (
                <StyledWrapper {...styles.productInfoWrapper}>
                    {showPartNumbers && (
                        <StyledWrapperX view={view} {...styles.partNumbersGridItem}>
                            <span className="product-attr-label">
                                {product.customerProductNumber
                                    ? siteMessage("Custom_Your_Part_#", "Your Part #:")
                                    : product?.properties?.specNumber?.length
                                        ? siteMessage("Custom_Spec_#", "Spec #:")
                                        : product.manufacturerItem
                                            ? siteMessage("Custom_MFR_#", "MFR #:")
                                            : siteMessage("Custom_Part_#", "EIS Part #:")}
                            </span>
                            &nbsp;
                            <span className="product-attr-value">
                                {product.customerProductNumber
                                    ? product.customerProductNumber
                                    : product?.properties?.specNumber?.length
                                        ? product.properties.specNumber
                                        : product.manufacturerItem
                                            ? product.manufacturerItem
                                            : product.productNumber}
                            </span>
                            <Tooltip
                                passAsChildren={true}
                                triggerComponent={
                                    <StyledWrapper>
                                        <Icon src={MiniMenu} color="secondary" />
                                    </StyledWrapper>
                                }>
                                <StyledWrapper className="popup-content-wrapper">
                                    {
                                        (
                                            <ProductPartNumbers
                                                productNumber={product.productNumber}
                                                customerProductNumber={product.customerProductNumber}
                                                manufacturerItem={product.manufacturerItem}
                                                extendedStyles={styles.partNumbersStyles}
                                                showLabel={true}
                                                {...({ modelNumber: product.detail?.modelNumber || "", specNumber: product.properties.specNumber || "" } as any)}

                                            />
                                        ) as any
                                    }
                                </StyledWrapper>
                            </Tooltip>
                        </StyledWrapperX>
                    )}
                    {variantDisplayTrait && (
                        <StyledWrapperX {...styles.swatchGridItem}>
                            <ProductListVariantSwatch product={productContext} variantTrait={variantDisplayTrait} />
                        </StyledWrapperX>
                    )}
                    <StyledWrapper
                        css={css`
                            margin-bottom: 16px;
                            min-height: 22px;
                        `}
                    >
                        {product?.brand?.name && showBrand && (
                            <>
                                <span className="product-attr-label">{translate("Brand:")}</span>&nbsp;
                                <Link href={product.brand.detailPagePath} className="product-attr-value">
                                    {product?.brand?.name}
                                </Link>
                            </>
                        )}
                    </StyledWrapper>
                    <StyledWrapper {...styles.priceWrapper}>
                        {showPrice && productContext.product.isVariantParent && displayHighLowPricesForParentProducts && (
                            <StyledWrapper {...styles.parentPrice}>
                                <StyledWrapper>{productContext.product?.properties?.lowPrice}</StyledWrapper>
                                {productContext.product?.properties?.highPrice &&
                                    productContext.product?.properties?.lowPrice !==
                                    productContext.product?.properties?.highPrice && (
                                        <StyledWrapper>
                                            &nbsp;-&nbsp;
                                            {productContext.product?.properties?.highPrice}
                                        </StyledWrapper>
                                    )}
                            </StyledWrapper>
                        )}
                        {showPrice && !productContext.product.isVariantParent && (
                            <>
                                <ProductPrice
                                    product={productContext}
                                    showLabel={false}
                                    showSavings={true}
                                    showSavingsAmount={settingsCollection?.productSettings.showSavingsAmount}
                                    showSavingsPercent={settingsCollection?.productSettings.showSavingsPercent}
                                    extendedStyles={styles.price}
                                />
                            </>
                        )}
                    </StyledWrapper>
                    {showTextBadges && textBadges && (
                        <StyledWrapper {...styles.textBadgeGridItem}>
                            <ProductTextBadges badgeList={textBadges} maxBadges={maxTextBadges ?? 4} />{" "}
                        </StyledWrapper>
                    )}
                    {showImageBadges && imageBadges && (
                        <StyledWrapper {...styles.imageBadgeGridItem}>
                            <ProductImageBadges badgeList={imageBadges} maxBadges={maxImageBadges ?? 4} />
                        </StyledWrapper>
                    )}
                    {showAvailability && (
                        <StyledWrapper>
                            <ProductContextAvailability extendedStyles={styles.availabilityStyles} />
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            )}
            {view === "List" && (
                <StyledWrapper {...styles.listInforWrapper}>
                    <StyledWrapper {...styles.numbersBrandWrapper}>
                        {showPartNumbers && (
                            <StyledWrapperX view={view} {...styles.partNumbersGridItem}>
                                <ProductPartNumbers
                                    productNumber={product.productNumber}
                                    customerProductNumber={product.customerProductNumber}
                                    manufacturerItem={product.manufacturerItem}
                                    extendedStyles={styles.partNumbersStyles}
                                    showLabel={true}
                                    {...({ modelNumber: product.detail?.modelNumber || "", specNumber: product.properties.specNumber || "" } as any)}

                                />
                            </StyledWrapperX>
                        )}
                        {variantDisplayTrait && (
                            <StyledWrapperX {...styles.swatchGridItem}>
                                <ProductListVariantSwatch product={productContext} variantTrait={variantDisplayTrait} />
                            </StyledWrapperX>
                        )}
                        {product.brand && showBrand && (
                            <StyledWrapper {...styles.brandGridItem}>
                                <ProductBrand
                                    brand={product.brand}
                                    extendedStyles={styles.brandStyles}
                                    showLogo={true}
                                />
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                    <StyledWrapper {...styles.badgesAvaiabilityWrapper}>
                        {settingsCollection.productSettings.showInventoryAvailability && showAvailability && (
                            <StyledWrapper
                                css={css`
                                    margin-bottom: 11px;
                                `}
                            >
                                <ProductContextAvailability extendedStyles={styles.listAvailabilityStyles} />
                            </StyledWrapper>
                        )}
                        {badges?.length > 0 && (
                            <StyledWrapper {...styles.badgesContainerStyle} className="info-list-view-badges">
                                {badges.map(
                                    (
                                        badge: { Image: string | undefined; DisplayName: string; Description: string },
                                        index: any,
                                    ) =>
                                        badge.Image && (
                                            <StyledWrapper
                                                css={css`
                                                    margin-left: 5px;
                                                    @media (max-width: 340px) {
                                                        min-width: 200px !important;
                                                    }
                                                    max-width: 300px;
                                                `}
                                                // eslint-disable-next-line react/no-array-index-key
                                                key={`badge${index}`}
                                            >
                                                <Tooltip
                                                    passAsChildren={true}
                                                    triggerComponent={
                                                        <StyledWrapper>
                                                            <LazyImage src={badge.Image} />
                                                        </StyledWrapper>
                                                    }>
                                                    <StyledWrapper className="popup-content-wrapper">
                                                        {badge.DisplayName === "PROP_65_CANCER"
                                                            ? product.properties.prop65Text ||
                                                            (siteMessage("Custom_Prop65DefaultText") as string)
                                                            : badge.Description}
                                                    </StyledWrapper>
                                                </Tooltip>
                                            </StyledWrapper>
                                        ),
                                )}
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(withProductContext(ProductListProductInformation));
